<template>
    <form-validation v-slot="{ bindings: validationBindings }">
        <flex-container v-if="loading">
            <spinner-loader />
        </flex-container>
        <flex-container
            v-else
            :padding="Size.xl"
            :gap="null"
            class="sources-view"
        >
            <flex-container class="sources-view__title">
                <text-decorator :size="FontSize.lg">
                    Analyse van jouw organisaties
                </text-decorator>
            </flex-container>

            <flex-container
                v-if="ownSources"
                :size="4"
                :gap="Size.xs"
                class="sources-view-splitter"
            >
                <text-heading sub>
                    Jouw website(s)
                </text-heading>
                <text-description>
                    Voeg de URL's van je website(s) toe, inclusief eventuele merken of extra websites die relevant zijn voor
                    je diensten en aanbod.
                </text-description>
                <flex-container class="sources-view__list">
                    <flex-container
                        justify-content="center"
                        :gap="Size.sm"
                    >
                        <flex-container
                            v-for="n in ownSources.length"
                            :key="n"
                        >
                            <flex-container :size="10" no-wrap>
                                <input-group
                                    v-model="ownSources[n - 1]"
                                    :schema="
                                        string()
                                            .url()
                                            .required()"
                                >
                                    <template #input>
                                        <text-input placeholder="https://www." />
                                    </template>
                                </input-group>
                            </flex-container>
                            <flex-container :size="'auto'">
                                <button-plain-icon
                                    v-tooltip="'Verwijderen'"
                                    :disabled="n === 1 && !ownSources[n - 1]"
                                    :icon="DeleteIcon"
                                    :color="Color.red500"
                                    class="sources-button"
                                    @click="deleteSource(n - 1)"
                                >
                                    Verwijderen
                                </button-plain-icon>
                            </flex-container>
                        </flex-container>
                        <button-text @click="addSource">
                            <svg-icon :icon="PlusIcon" />
                            <span>Extra toevoegen</span>
                        </button-text>
                    </flex-container>
                </flex-container>
            </flex-container>

            <flex-container
                v-if="thirdPartySources"
                :size="6"
                :gap="Size.xs"
            >
                <text-heading sub>
                    Websites van derden waar jij mee samenwerkt voor up- of crossell
                </text-heading>
                <text-description>
                    Voeg hier de URL's toe van jouw partners en geef aan voor welke pijplijn jij met ze samenwerkt.<br>
                    Dit is optioneel.
                </text-description>
                <flex-container
                    v-if="availablePipelines"
                    class="sources-view__list"
                    :gap="Size.sm"
                    justify-content="center"
                >
                    <flex-container
                        v-for="n in thirdPartySources.length"
                        :key="n"
                        no-wrap
                    >
                        <flex-container :size="6">
                            <input-group
                                v-model="thirdPartySources[n - 1].source"
                                :schema="string().url()"
                            >
                                <template #input>
                                    <text-input placeholder="https://www." />
                                </template>
                            </input-group>
                        </flex-container>
                        <flex-container :size="4">
                            <input-group v-model="thirdPartySources[n - 1].pipeline">
                                <template #input>
                                    <pipeline-select-input :pipelines="availablePipelines.data" />
                                </template>
                            </input-group>
                        </flex-container>
                        <flex-container :size="'auto'">
                            <button-plain-icon
                                v-tooltip="'Verwijderen'"
                                :disabled="n === 1 && thirdPartySources[n - 1].pipeline === null && !thirdPartySources[n - 1].source"
                                :icon="DeleteIcon"
                                :color="Color.red500"
                                class="sources-button"
                                @click="deleteThirdPartySource(n - 1)"
                            >
                                Verwijderen
                            </button-plain-icon>
                        </flex-container>
                    </flex-container>
                    <button-text @click="addThirdPartySource">
                        <svg-icon :icon="PlusIcon" />
                        <span>Extra toevoegen</span>
                    </button-text>
                </flex-container>
            </flex-container>

            <flex-container
                class="sources-view__save"
                justify-content="flex-end"
                align-items="center"
            >
                <button-text v-if="showBackButton" @click="redirectToRules">
                    Ga terug
                </button-text>
                <button-block :disabled="!validationBindings.isValid.value" @loading="save($event)">
                    Opslaan
                </button-block>
            </flex-container>
        </flex-container>
    </form-validation>
</template>

<script setup lang="ts">
    import { ButtonState, type ButtonStateChangedEvent, Color, FontSize, Size, TextDescription, TextHeading, ButtonPlainIcon } from '@keyvue/one-ui';
    import PlusIcon from '@keyvue/one-material-icons/icons/add.svg?component';
    import DeleteIcon from '@keyvue/one-material-icons/icons/delete.svg?component';
    import { ref, onMounted } from 'vue';
    import PipelineSelectInput from '@components/Inputs/PipelineSelectInput.vue';
    import useClient from '@composables/useClient';
    import { AISettings, Pipeline, ThirdPartySource, ChangeAISettingsCommand } from '@keyvue/one-api-client';
    import useNotify from '@composables/useNotify';
    import { useRouter } from 'vue-router';
    import { string } from 'yup';

    const router = useRouter();
    const { client, currentCompany } = useClient();
    const { notifySaving } = useNotify();
    const availablePipelines = await client.getSubResources(currentCompany.value, Pipeline, 'available-pipelines');

    const ownSources = ref<string[]>([]);
    const thirdPartySources = ref<{ pipeline: Pipeline | null, source: string }[]>([]);
    const showBackButton = ref(false);
    const loading = ref(false);

    const redirectToRules = () => {
        router.push({ name: 'settings-virtual-colleague-ai-rules' });
    };

    const deleteSource = (index: number) => {
        ownSources.value.splice(index, 1);
        createMandatoryEntries();
    };

    const deleteThirdPartySource = (index: number) => {
        thirdPartySources.value.splice(index, 1);
        createMandatoryEntries();
    };

    const addSource = () => {
        ownSources.value.push('');
    };

    const addThirdPartySource = () => {
        thirdPartySources.value.push({ pipeline: null, source: '' });
    };

    const createMandatoryEntries = () => {
        if (ownSources.value.length <= 0) {
            addSource();
        }

        if (thirdPartySources.value.length <= 0) {
            addThirdPartySource();
        }
    };

    const save = async (event: ButtonStateChangedEvent) => {
        const closeNotification = notifySaving('De bronnen worden opgeslagen.', 0);

        /** creating a command with filtered empty inputs and mapped pipelines to pipelineIds */
        const command = new ChangeAISettingsCommand(
            currentCompany.value.id,
            ownSources.value.filter((source) => (
                !!source
            )),
            thirdPartySources.value.filter((thirdPartySource) => (
                !!thirdPartySource.source && thirdPartySource.pipeline !== null
            )).map((thirdPartySource) => ThirdPartySource.fromObject({
                source: thirdPartySource.source,
                pipelineId: thirdPartySource.pipeline?.id,
            })),
        );

        await client.dispatch(command);

        closeNotification();
        event.button.setState(ButtonState.completed);

        redirectToRules();
    };

    onMounted(async () => {
        loading.value = true;
        const settings = await client.get(AISettings, currentCompany.value.id);
        ownSources.value = settings.ownSources;
        thirdPartySources.value = settings.thirdPartySources.map((thirdPartySource) => ({
            source: thirdPartySource.source,
            pipeline: availablePipelines.data.find((pipeline) => pipeline.id.equals(thirdPartySource.pipelineId)) || null,
        }));

        createMandatoryEntries();

        showBackButton.value = settings.ownSources.filter(
            (source) => !!source,
        ).length > 0;

        loading.value = false;
    });
</script>

<style lang="scss" scoped>
.sources-view {
    padding-top: 0 !important;

    &__title {
        padding-bottom: size(md);
    }

    &__list {
        padding-top: size(md);
    }

    &__save {
        padding-top: 60px;
    }
}

.sources-view-splitter {
    position: relative;

    padding-right: size(xl);

    & + div {
        padding-left: size(xl);
    }

    &::after {
        content: '';
        position: absolute;
        right: 0;
        bottom: 0;
        height: 100%;
        width: 1px;
        background-color: color(gray-200);
    }
}

.sources-button {
    height: size(xl);
}
</style>
